import React, { useState, useEffect, useCallback, useRef, useId } from "react";
import { useDispatch } from "react-redux";
import { motion, AnimatePresence } from "motion/react";

import UploadStep from "./UploadStep";
import ValidationStep from "./ValidationStep";
import EditStep from "./EditStep";
import TeamsValidationStep from "./TeamsValidationStep";
import TeamsEditStep from "./TeamsEditStep";
import SubmitStep from "./SubmitStep";
import FailedRecordsStep from "./FailedRecordsStep";
import useUploadsQueries from "../../../store/uploads/uploadsQueries";
import useTeamHierarchyQueries from "../../../store/teams/teamsQueries";
import { uploadMutation } from "../../../store/uploads/uploadMutation";
import "./UploadModal.css";

const UploadPopUp = ({
  isOpen = true, // Default to true for backward compatibility
  handleCloseModal,
  schoolId,
  selectedEvent,
  uploadType, // "eventCreate" | "eventReschedule" | "eventDelete" | "studentRegister" | "globalStudentsRegister" | "uploadOfflineResponses" | "hierarchyUpload"
  selectedTab,
  onUploadSuccess, // Callback to trigger data refresh
}) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validData, setValidData] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [jobId, setJobId] = useState();
  const [uploadStatusData, setUploadStatusData] = useState({});
  const [apiValidationError, setApiValidationError] = useState(null);
  const [uploadSuccessful, setUploadSuccessful] = useState(false);

  const dialogRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);

  const titleId = useId();
  const descriptionId = useId();

  const {
    handleBulkRegister,
    handleBulkGlobalRegister,
    handleBulkUpdate,
    handleBulkEventCreate,
    handleBulkEventReschedule,
    handleBulkEventDelete,
    handleBulkUploadOfflineResponses,
  } = useUploadsQueries();

  const { handleUpload: handleHierarchyUpload } = useTeamHierarchyQueries();

  const getUploadTypeTitle = () => {
    switch (uploadType) {
      case "eventCreate":
        return "Bulk Event Creation";
      case "eventReschedule":
        return "Bulk Event Rescheduling";
      case "eventDelete":
        return "Bulk Event Deletion";
      case "studentRegister":
        return "Bulk Student Registration";
      case "uploadOfflineResponses":
        return "Bulk Upload Offline Responses";
      case "globalStudentsRegister":
        return "Bulk Student Registration";
      case "hierarchyUpload":
        return "Bulk Team Hierarchy Upload";
      default:
        return "Import File";
    }
  };

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  // Animation variants for overlay
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: prefersReducedMotion ? 0 : 0.3 },
    },
    exit: {
      opacity: 0,
      transition: { duration: prefersReducedMotion ? 0 : 0.3 },
    },
  };

  // Animation variants for modal content (adapted for flex-centered layout)
  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: prefersReducedMotion ? 1 : 0.9,
      y: prefersReducedMotion ? 0 : 30,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0 : 0.4,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      scale: prefersReducedMotion ? 1 : 0.9,
      y: prefersReducedMotion ? 0 : -30,
      transition: {
        duration: prefersReducedMotion ? 0 : 0.3,
        ease: "easeIn",
      },
    },
  };

  const resetModalState = useCallback(() => {
    const initialStep =
      uploadType === "studentRegister" && selectedTab === "Onboarded Students"
        ? 0
        : 1;

    setStep(initialStep);
    setFile(null);
    setCsvData([]);
    setValidationErrors([]);
    setValidData([]);
    setIsValid(false);
    setIsValidating(true);
    setLoading(false);
    setLoadingStatus(false);
    setUploadMsg("");
    setJobId(undefined);
    setUploadStatusData({});
    setApiValidationError(null);
    setUploadSuccessful(false);
    dispatch(uploadMutation.util.resetApiState());
  }, [dispatch, selectedTab, uploadType]);

  const handleModalClose = useCallback(() => {
    if (
      uploadSuccessful &&
      uploadType === "studentRegister" &&
      onUploadSuccess
    ) {
      onUploadSuccess();
    }

    // Always reset modal state when closing
    resetModalState();

    // Close the modal
    handleCloseModal();
  }, [
    handleCloseModal,
    onUploadSuccess,
    resetModalState,
    uploadSuccessful,
    uploadType,
  ]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleModalClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleModalClose]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    resetModalState();

    return undefined;
  }, [isOpen, resetModalState]);

  useEffect(() => {
    if (typeof document === "undefined" || !isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (typeof document === "undefined" || !isOpen) {
      const previouslyFocused = previouslyFocusedElementRef.current;
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
      previouslyFocusedElementRef.current = null;
      return undefined;
    }

    const dialogNode = dialogRef.current;
    if (!dialogNode) {
      return undefined;
    }

    previouslyFocusedElementRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const focusableSelector =
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

    const getFocusableElements = () =>
      Array.from(dialogNode.querySelectorAll(focusableSelector)).filter(
        (element) =>
          element instanceof HTMLElement &&
          !element.hasAttribute("disabled") &&
          element.getAttribute("tabindex") !== "-1" &&
          element.getAttribute("aria-hidden") !== "true"
      );

    const focusFirstElement = () => {
      const focusableElements = getFocusableElements();
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      } else {
        dialogNode.focus();
      }
    };

    focusFirstElement();

    const handleKeyDown = (event) => {
      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        event.preventDefault();
        dialogNode.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const isShiftPressed = event.shiftKey;
      const activeElement = document.activeElement;

      if (!isShiftPressed && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      } else if (
        isShiftPressed &&
        (activeElement === firstElement || activeElement === dialogNode)
      ) {
        event.preventDefault();
        lastElement.focus();
      }
    };

    dialogNode.addEventListener("keydown", handleKeyDown);

    return () => {
      dialogNode.removeEventListener("keydown", handleKeyDown);
      const previouslyFocused = previouslyFocusedElementRef.current;
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
      previouslyFocusedElementRef.current = null;
    };
  }, [isOpen]);

  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  const handleFileChange = (data) => {
    setCsvData(data);
    nextStep();
  };

  const handleValidationResult = useCallback((valid, errors) => {
    setValidData(valid);
    setValidationErrors(errors);
    setIsValid(errors.length === 0);
    setIsValidating(false);
  }, []);

  const handleUpdateCsvData = (data) => {
    setCsvData(data);
    nextStep();
  };

  const handleSubmit = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append("fileobject", file);

    if (
      uploadType === "studentRegister" ||
      uploadType === "globalStudentsRegister"
    ) {
      formData.append(
        "event_scheduled",
        selectedEvent ? selectedEvent.scheduled_events.join(",") : ""
      );
    }

    try {
      let response;
      switch (uploadType) {
        case "eventCreate":
          response = await handleBulkEventCreate(schoolId, formData);
          break;
        case "eventReschedule":
          response = await handleBulkEventReschedule(schoolId, formData);
          break;
        case "eventDelete":
          response = await handleBulkEventDelete(schoolId, formData);
          break;
        case "studentRegister":
          response = await handleBulkRegister(schoolId, formData);
          break;
        case "uploadOfflineResponses":
          response = await handleBulkUploadOfflineResponses(formData);
          break;
        case "globalStudentsRegister":
          response = await handleBulkGlobalRegister(formData);
          break;
        case "hierarchyUpload":
          response = await handleHierarchyUpload(formData);
          break;
        default:
          response = await handleBulkUpdate(schoolId, formData);
      }

      // Offline responses: treat status === "failed" as validation failure data
      if (uploadType === "uploadOfflineResponses") {
        if (response?.status === "failed") {
          setUploadStatusData({
            status: "failed",
            detail: response?.detail || [],
          });
          setUploadMsg(response?.message || "Validation Failed");
          // Don't set jobId for validation failures - no job is created
        } else {
          setUploadMsg(
            response?.message || response?.id || "Upload Successful"
          );
          // Only set jobId if it exists (for successful uploads that create a job)
          if (response?.jobId) {
            setJobId(response.jobId);
          }
        }
        return;
      }

      // All other upload types
      setUploadMsg(response?.id || response?.message || "Upload Successful");
      setJobId(response?.jobId || "");

      // Mark upload as successful for student registration
      if (uploadType === "studentRegister") {
        setUploadSuccessful(true);
      }
    } catch (error) {
      console.error("Error:", error);

      if (error?.name === "ApiValidationError") {
        setApiValidationError(error.data);
        setUploadMsg("Validation Failed");
      } else {
        const fallbackMessage =
          (typeof error?.message === "string" && error.message) ||
          error?.data?.message ||
          error?.data?.detail ||
          "Upload failed";
        setUploadMsg(`Error: ${fallbackMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const upoloadeStatus = async () => {
    setLoadingStatus(true);
    try {
      let result;
      if (uploadType === "uploadOfflineResponses") {
        result = await dispatch(
          uploadMutation.endpoints.getOfflineUploadStatus.initiate(jobId, {
            forceRefetch: true,
          })
        );
      } else {
        result = await dispatch(
          uploadMutation.endpoints.getBulkRecordStatus.initiate(jobId, {
            forceRefetch: true,
          })
        );
      }

      if (result?.data) {
        setUploadStatusData(result.data);
      } else {
        throw new Error();
      }
    } catch (error) {
      setUploadStatusData({
        status: "Bulk upload job not found",
      });
      console.error("Error fetching status:", error);
    } finally {
      setLoadingStatus(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className="overlay upload-modal-overlay"
          onClick={handleModalClose}
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            className="overlay-content upload-modal-dialog"
            onClick={(e) => e.stopPropagation()}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            ref={dialogRef}
            tabIndex={-1}
          >
            <h1 id={titleId}>{getUploadTypeTitle()}</h1>
            <div className="line"></div>
            <div className="uploadModal-content" id={descriptionId}>
              {step === 0 && selectedTab === "Onboarded Students" && (
                <div>
                  <p className="declaration">
                    <span>Attention:</span> This process registers students with
                    the school.
                    <br /> You will need to assign them to events separately
                    from events tab.
                  </p>
                </div>
              )}
              {step === 1 && (
                <UploadStep
                  setFile={setFile}
                  uploadType={uploadType}
                  handleFileChange={handleFileChange}
                />
              )}
              {step === 2 && uploadType === "hierarchyUpload" ? (
                <TeamsValidationStep
                  csvData={csvData}
                  handleValidationResult={handleValidationResult}
                  schoolId={schoolId}
                />
              ) : (
                step === 2 && (
                  <ValidationStep
                    csvData={csvData}
                    handleValidationResult={handleValidationResult}
                    schoolId={schoolId}
                    uploadType={uploadType}
                  />
                )
              )}
              {step === 3 && uploadType === "hierarchyUpload" ? (
                <TeamsEditStep
                  csvData={csvData}
                  validationErrors={validationErrors}
                  handleUpdateCsvData={handleUpdateCsvData}
                />
              ) : (
                step === 3 && (
                  <EditStep
                    csvData={csvData}
                    validationErrors={validationErrors}
                    handleUpdateCsvData={handleUpdateCsvData}
                    uploadType={uploadType}
                  />
                )
              )}
              {step === 4 && (
                <SubmitStep
                  loading={loading}
                  uploadMsg={uploadMsg}
                  validData={validData}
                  handleSubmit={handleSubmit}
                  upoloadeStatus={upoloadeStatus}
                  uploadStatusData={uploadStatusData}
                  jobId={jobId}
                  loadingStatus={loadingStatus}
                  apiValidationError={apiValidationError}
                />
              )}
              {step === 5 && (
                <FailedRecordsStep
                  uploadStatusData={uploadStatusData}
                  uploadType={uploadType}
                />
              )}
            </div>
            <div className="popupobutnCont">
              {step < 1 && (
                <button onClick={() => setStep(1)} className="saveBtn">
                  OK
                </button>
              )}
              {step < 2 && (
                <button
                  onClick={handleModalClose}
                  className="closeBtn"
                  disabled={step === 2 && isValidating}
                >
                  Close
                </button>
              )}
              {step > 2 && (
                <button
                  onClick={handleModalClose}
                  className="closeBtn"
                  disabled={step === 2 && isValidating}
                >
                  Close
                </button>
              )}
              {/* Standard Back button - hidden at step 4 and step 5 */}
              {step > 1 && step !== 4 && step !== 5 && (
                <button
                  onClick={prevStep}
                  className="closeBtn"
                  disabled={step === 2 && isValidating}
                >
                  Back
                </button>
              )}
              {step === 2 && !isValidating && !isValid && (
                <button onClick={() => setStep(3)} className="saveBtn">
                  View
                </button>
              )}
              {step === 2 && !isValidating && isValid && (
                <button onClick={() => setStep(4)} className="saveBtn">
                  Next
                </button>
              )}
              {step === 4 && uploadMsg === "" && (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="saveBtn"
                >
                  Submit
                </button>
              )}
              {step === 4 &&
                uploadStatusData?.status?.toLowerCase?.() === "failed" && (
                  <button onClick={() => setStep(5)} className="saveBtn">
                    View Failed Records
                  </button>
                )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UploadPopUp;
